<template>
    <VTextField
        v-model="displayVal"
        :variant="variant"
        :label="label"
        :density="density"
        :hint="hint"
        :prefix="prefix"
        :suffix="suffix"
        :messages="messages"
        :disabled="disabled"
        :clearable="clearable"
        :readonly="readonly"
        :loading="loading"
        :errorMessages="errorMessages"
        v-maska="mask"
        :placeholder="placeholder"
        @blur="onBlur">
        <template #append-inner v-if="tooltip">
            <Icon v-tippy="tooltip" type="InfoCircle" color="black" size="xs" />
        </template>
    </VTextField>
</template>

<script lang="ts">
    import { defineComponent, ref, PropType, watch } from 'vue';
    import { VTextField } from 'vuetify/components';
    import { Variant, Density } from './DateInput.interfaces';
    import { isValidDate } from '@shared/helpers/datetime/datetime';
    import {
        getLocaleIntlDateFormat,
        getTranslatedDateFormat,
    } from '@shared/helpers/datetime/datetime.formatters';
    import { useDate } from 'vuetify';
    import { MaskInputOptions } from 'maska';
    import Icon from '@ui/components/Icon/Icon.vue';

    export default defineComponent({
        name: 'DateInput',
        components: { Icon, VTextField },
        emits: ['update:modelValue'],
        props: {
            modelValue: { type: String },
            label: String,
            hint: String,
            prefix: String,
            suffix: String,
            tooltip: String,
            loading: { type: Boolean as PropType<boolean>, default: false },
            disabled: { type: Boolean as PropType<boolean>, default: false },
            clearable: { type: Boolean as PropType<boolean>, default: false },
            readonly: { type: Boolean as PropType<boolean>, default: false },
            messages: { type: Array as PropType<string[]> },
            errorMessages: {
                type: [Array, String] as PropType<string[] | string>,
            },
            variant: { type: String as PropType<Variant>, default: 'outlined' },
            density: { type: String as PropType<Density>, default: 'default' },
        },
        setup(props, { emit }) {
            const dateAdapter = useDate();
            const isoFormat = 'yyyy-MM-dd';
            const format = getLocaleIntlDateFormat(dateAdapter.locale);
            const placeholder = getTranslatedDateFormat(format);
            const displayVal = ref<string | undefined>();

            const mask = ref<MaskInputOptions>({
                mask: format.replace(/[a-zA-Zå]/g, '#'),
                eager: true,
            });

            const validateDate = (dateStr: string): boolean => {
                const parts = dateStr.match(/(\d+)/g);
                return Boolean(parts && parts.length === 3) && dateStr.length === 10;
            };

            watch(
                () => displayVal.value,
                (newValue) => {
                    onUpdateValue(newValue);
                }
            );

            watch(
                () => props.modelValue,
                (newValue, prevValue) => {
                    if (newValue && newValue !== prevValue) {
                        displayVal.value = getParsedDate(newValue, isoFormat, format);
                    }
                },
                { immediate: true }
            );

            const onBlur = () => {
                onUpdateValue(displayVal.value);
            };

            const onUpdateValue = (date?: string) => {
                if (date && validateDate(date)) {
                    emit('update:modelValue', getParsedDate(date, format, isoFormat));
                } else {
                    emit('update:modelValue', undefined);
                }
            };
            function getParsedDate(
                dateString: string,
                formatString: string,
                outputFormatString: string
            ): string | undefined {
                function addLeadingZero(str: string): string {
                    return str.padStart(2, '0');
                }

                const formatParts = formatString.match(/(yyyy|MM|dd|.)/g);
                const dateParts: { [key: string]: string } = {};
                let currentIndex = 0;

                if (!formatParts) {
                    return undefined;
                }

                formatParts.forEach((part) => {
                    const partLength =
                        part === 'yyyy' || part === 'MM' || part === 'dd' ? part.length : 1;
                    dateParts[part] = dateString.substring(
                        currentIndex,
                        currentIndex + partLength
                    );
                    currentIndex += partLength;
                });

                if (!dateParts['yyyy'] || !dateParts['MM'] || !dateParts['dd']) {
                    return undefined;
                }

                const dateStr = `${dateParts['yyyy']}-${addLeadingZero(dateParts['MM'])}-${addLeadingZero(dateParts['dd'])}`;

                if (isValidDate(dateStr)) {
                    let outputDate = outputFormatString;
                    outputDate = outputDate.replace('yyyy', dateParts['yyyy']);
                    outputDate = outputDate.replace('MM', addLeadingZero(dateParts['MM']));
                    outputDate = outputDate.replace('dd', addLeadingZero(dateParts['dd']));
                    return outputDate;
                }

                return undefined;
            }

            return {
                displayVal,
                onBlur,
                mask,
                placeholder,
            };
        },
    });
</script>
